const initializedSections = new WeakSet();

const initWhatWeDoAccordion = (scope = document) => {
  const sections = scope.querySelectorAll('.what-we-do-sec');

  sections.forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const accordion = section.querySelector('[data-what-we-do-accordion]');
    const mediaStage = section.querySelector('.what-we-do-sec__media');
    const items = Array.from(section.querySelectorAll('.what-we-do-sec__item'));
    const mediaItems = Array.from(
      section.querySelectorAll('.what-we-do-sec__media-picture')
    );
    const mobileSlots = new Map(
      items
        .map((item, index) => {
          const mediaIndex = Number(item.dataset.whatWeDoItem);
          const slot = item.querySelector('[data-what-we-do-mobile-slot]');

          return [Number.isNaN(mediaIndex) ? index : mediaIndex, slot];
        })
        .filter(([, slot]) => slot)
    );
    const mobileMediaQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 1023px)')
        : null;

    if (!accordion || !items.length) {
      return;
    }

    initializedSections.add(section);

    let isMobileLayout = false;

    const getMediaIndex = (element, fallbackIndex, dataAttribute) => {
      const mediaIndex = Number(element.dataset[dataAttribute]);

      return Number.isNaN(mediaIndex) ? fallbackIndex : mediaIndex;
    };

    const updateMobileMediaHeight = () => {
      if (!isMobileLayout) {
        return;
      }

      mobileSlots.forEach((slot) => {
        slot.style.setProperty(
          '--what-we-do-mobile-media-height',
          `${slot.getBoundingClientRect().width}px`
        );
      });
    };

    const moveMediaToMobileSlots = () => {
      mediaItems.forEach((media, index) => {
        const mediaIndex = getMediaIndex(media, index, 'whatWeDoMedia');
        const mobileSlot = mobileSlots.get(mediaIndex);

        if (mobileSlot) {
          mobileSlot.append(media);
        }
      });
    };

    const restoreMediaStage = () => {
      if (mediaStage) {
        mediaStage.append(...mediaItems);
      }

      mobileSlots.forEach((slot) => {
        slot.style.removeProperty('--what-we-do-mobile-media-height');
      });
    };

    const syncMediaLayout = () => {
      const shouldUseMobileLayout = Boolean(mobileMediaQuery?.matches);

      if (shouldUseMobileLayout && !isMobileLayout) {
        moveMediaToMobileSlots();
        isMobileLayout = true;
      } else if (!shouldUseMobileLayout && isMobileLayout) {
        restoreMediaStage();
        isMobileLayout = false;
      }

      updateMobileMediaHeight();
    };

    const updatePanelHeight = (item) => {
      const panel = item.querySelector('.what-we-do-sec__panel');

      if (!panel) {
        return;
      }

      panel.style.setProperty(
        '--what-we-do-panel-height',
        `${panel.scrollHeight}px`
      );
    };

    const setItemState = (item, isActive) => {
      const toggle = item.querySelector('.what-we-do-sec__toggle');
      const panel = item.querySelector('.what-we-do-sec__panel');

      item.classList.toggle('is-active', isActive);

      if (toggle) {
        toggle.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }

      if (panel) {
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      }

      if (isActive) {
        updatePanelHeight(item);
      }
    };

    const setActiveItem = (activeItem) => {
      const activeIndex = items.indexOf(activeItem);
      const mediaIndex = getMediaIndex(
        activeItem,
        activeIndex,
        'whatWeDoItem'
      );

      items.forEach((item) => {
        setItemState(item, item === activeItem);
      });

      mediaItems.forEach((media, index) => {
        media.classList.toggle(
          'is-active',
          getMediaIndex(media, index, 'whatWeDoMedia') === mediaIndex
        );
      });

      updateMobileMediaHeight();
    };

    const currentItem =
      items.find((item) => item.classList.contains('is-active')) || items[0];

    requestAnimationFrame(() => {
      syncMediaLayout();
      setActiveItem(currentItem);
    });

    accordion.addEventListener('click', (event) => {
      const toggle = event.target.closest('.what-we-do-sec__toggle');

      if (!toggle || !accordion.contains(toggle)) {
        return;
      }

      const item = toggle.closest('.what-we-do-sec__item');

      if (!item || item.classList.contains('is-active')) {
        return;
      }

      setActiveItem(item);
    });

    let resizeFrame = 0;

    const updateActivePanel = () => {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame);
      }

      resizeFrame = requestAnimationFrame(() => {
        syncMediaLayout();

        const activeItem = items.find((item) =>
          item.classList.contains('is-active')
        );

        if (activeItem) {
          updatePanelHeight(activeItem);
        }

        resizeFrame = 0;
      });
    };

    window.addEventListener('resize', updateActivePanel, { passive: true });

    if (mobileMediaQuery) {
      const handleMediaQueryChange = () => {
        syncMediaLayout();
        updateActivePanel();
      };

      if (typeof mobileMediaQuery.addEventListener === 'function') {
        mobileMediaQuery.addEventListener('change', handleMediaQueryChange);
      } else if (typeof mobileMediaQuery.addListener === 'function') {
        mobileMediaQuery.addListener(handleMediaQueryChange);
      }
    }

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateActivePanel);

      items.forEach((item) => {
        const text = item.querySelector('.what-we-do-sec__text');

        if (text) {
          resizeObserver.observe(text);
        }
      });

      resizeObserver.observe(accordion);

      section.whatWeDoResizeObserver = resizeObserver;
    }
  });
};

initWhatWeDoAccordion();

window.initWhatWeDoAccordion = initWhatWeDoAccordion;
